import { memo, useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  ModalBody,
  ModalContent,
  Modal,
  ModalHeader,
  ModalFooter,
  Tabs,
  Tab,
  useDisclosure,
  Checkbox,
} from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useUpdateCourseMutation,
  useGetCourseByIdQuery,
  useGetCoursesQuery,
  useDeleteCourseMutation,
  CourseType,
  CourseLevel,
  CoursePathway,
  COURSE_TYPE_LABELS,
  COURSE_LEVEL_LABELS,
  COURSE_PATHWAY_LABELS,
  flattenCourseGroups,
  isDegreeLikeCourseType,
  createCourseFormSchema,
  getCoursePointErrors,
  hasCoursePointErrors,
  getCourseFormErrorTab,
  getCourseServerErrorField,
  COURSE_FORM_TAB_FIELDS,
  COURSE_TEXT_LIMITS,
  COURSE_IMAGE_ACCEPT,
  COURSE_MAX_BENEFITS,
  COURSE_MAX_RECOMMENDED,
  COURSE_RECOMMENDED_LIMIT_MESSAGE,
  COURSE_RECOMMENDED_SELECTED_MESSAGE,
} from '@/entities/Courses';
import { useGetAcademicStaffQuery, AcademicStaff } from '@/entities/AcademicStaff';
import { useGetAdminCategoriesQuery } from '@/entities/Categories';
import { AppButton } from '@/shared/ui/AppButton';
import {
  CustomCheckboxField,
  CustomInput,
  CustomSelect,
  CustomFileInput,
  CustomTextarea,
} from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import { dateToDateValue } from '@/shared/lib/utils/dateAdapters';
import { MessageModal } from '@/shared/ui/Modal';
import { Svg } from '@/shared/ui/Svg';
import EditIcon from '@/shared/assets/icons/edit.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import styles from './EditCourseModal.module.scss';

interface EditCourseModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  courseId: number;
  onSuccess?: () => void;
}

interface BenefitPoint {
  id: string;
  title: string;
  description: string;
}

interface TargetAudiencePoint {
  id: string;
  title: string;
  description: string;
}

interface CourseFormData {
  title: string;
  type: string;
  level: string;
  category: string;
  leadTeacher: string;
  description: string;
  price: number;
  discountPrice?: number;
  saleDateRange?: { start: CalendarDate; end: CalendarDate } | null;
  durationFullTime: number;
  durationPartTime: number;
  certificateDescription?: string;
  seoTitle?: string;
  seoDescription?: string;
  isAI: boolean;
  pathway: string;
  paymentLink?: string;
  imageFile: File | null;
  previewFile?: File | null;
  benefits: BenefitPoint[];
  targetAudience: TargetAudiencePoint[];
  recommendedCourses: number[];
}


const courseSchema = createCourseFormSchema({ isCreate: false });

const COURSE_TYPES = Object.values(CourseType).map((type) => ({
  key: type,
  label: COURSE_TYPE_LABELS[type],
}));

const ALL_COURSE_LEVELS = Object.values(CourseLevel).map((level) => ({
  key: level,
  label: COURSE_LEVEL_LABELS[level],
}));

const LEVELS_FOR_SHORT = ALL_COURSE_LEVELS.filter((l) => l.key === CourseLevel.SHORT_COURSE);
const LEVELS_FOR_DEGREE = ALL_COURSE_LEVELS.filter((l) => l.key !== CourseLevel.SHORT_COURSE);

const COURSE_PATHWAYS = Object.values(CoursePathway).map((pathway) => ({
  key: pathway,
  label: COURSE_PATHWAY_LABELS[pathway],
}));

export const EditCourseModal = memo(
  ({ isOpen, onOpenChange, courseId, onSuccess }: EditCourseModalProps) => {
    const { toast } = useToast();
    const { canEntity } = useAdminAccess();
    const canCourseDelete = canEntity('course', 'delete');
    const [updateCourse, { isLoading }] = useUpdateCourseMutation();
    const [deleteCourse, { isLoading: isDeleting }] = useDeleteCourseMutation();
    const [activeTab, setActiveTab] = useState<string>('cover');
    const [selectedSection, setSelectedSection] = useState<string | null>(null);
    const {
      isOpen: isDeleteModalOpen,
      onOpen: onDeleteModalOpen,
      onOpenChange: onDeleteModalOpenChange,
    } = useDisclosure();
    const [benefits, setBenefits] = useState<BenefitPoint[]>([]);
    const [targetAudience, setTargetAudience] = useState<TargetAudiencePoint[]>([]);
    const [recommendedCourses, setRecommendedCourses] = useState<number[]>([]);
    const [initialRecommendedCourses, setInitialRecommendedCourses] = useState<number[]>([]);
    const [showDiscountPrice, setShowDiscountPrice] = useState(false);
    const [isCourseSelectModalOpen, setIsCourseSelectModalOpen] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
    const [currentPreviewUrl, setCurrentPreviewUrl] = useState<string | null>(null);
    const [showPointErrors, setShowPointErrors] = useState(false);
    const [showRecommendedLimitError, setShowRecommendedLimitError] = useState(false);
    const [showSelectLimitError, setShowSelectLimitError] = useState(false);

    const { data: courseData, isLoading: isCourseLoading } = useGetCourseByIdQuery(courseId, {
      skip: !isOpen || !courseId,
    });

    const { data: academicStaffData } = useGetAcademicStaffQuery(
      { page: 1, pageSize: 1000 },
      { skip: !isOpen },
    );
    const academicStaff = academicStaffData?.data?.academicStaff || [];

    const { data: coursesData } = useGetCoursesQuery(undefined, { skip: !isOpen });
    const allCourses = flattenCourseGroups(coursesData?.data);
    const { data: categoriesData } = useGetAdminCategoriesQuery(undefined, { skip: !isOpen });

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      watch,
      setValue,
      getValues,
      trigger,
      setError,
    } = useForm<CourseFormData>({
      resolver: yupResolver(courseSchema) as any,
      mode: 'onChange',
      defaultValues: {
        title: '',
        type: '',
        level: '',
        category: '',
        leadTeacher: '',
        description: '',
        price: 0,
        discountPrice: undefined,
        saleDateRange: null,
        durationFullTime: 0,
        durationPartTime: 0,
        certificateDescription: '',
        seoTitle: '',
        seoDescription: '',
        isAI: false,
        pathway: '',
        paymentLink: '',
        imageFile: null,
        previewFile: null,
        benefits: [],
        targetAudience: [],
        recommendedCourses: [],
      },
    });

    const watchedType = watch('type');
    const categoryItems = useMemo(
      () =>
        (categoriesData?.data?.categories ?? [])
          .filter((category) => category.isActive)
          .map((category) => ({
            key: String(category.id),
            label: category.title,
          })),
      [categoriesData?.data?.categories],
    );

    const getCategoryId = (categoryValue: unknown): string => {
      if (typeof categoryValue === 'number') return String(categoryValue);
      if (typeof categoryValue === 'string') {
        return /^\d+$/.test(categoryValue) ? categoryValue : '';
      }
      if (categoryValue && typeof categoryValue === 'object' && 'id' in categoryValue) {
        const id = (categoryValue as { id?: unknown }).id;
        if (typeof id === 'number') return String(id);
        if (typeof id === 'string' && /^\d+$/.test(id)) return id;
      }
      return '';
    };

    const getCategoryTitle = (categoryValue: unknown): string => {
      if (categoryValue && typeof categoryValue === 'object' && 'title' in categoryValue) {
        const title = (categoryValue as { title?: unknown }).title;
        return typeof title === 'string' ? title : '';
      }
      return '';
    };

    const levelItems = useMemo(() => {
      if (watchedType === CourseType.SHORT) {
        return LEVELS_FOR_SHORT;
      }
      if (isDegreeLikeCourseType(watchedType)) {
        return LEVELS_FOR_DEGREE;
      }
      return [];
    }, [watchedType]);

    useEffect(() => {
      if (!watchedType) return;
      const allowedLevels =
        watchedType === CourseType.SHORT
          ? [CourseLevel.SHORT_COURSE]
          : isDegreeLikeCourseType(watchedType)
            ? Object.values(CourseLevel).filter((l) => l !== CourseLevel.SHORT_COURSE)
            : [];
      const currentLevel = getValues('level');
      if (currentLevel && !allowedLevels.includes(currentLevel as CourseLevel)) {
        setValue('level', '');
      }
    }, [watchedType, setValue, getValues]);

    useEffect(() => {
      if (courseData?.data?.course && isOpen) {
        const course = courseData.data.course;
        const saleStart = course.saleStart ? dateToDateValue(new Date(course.saleStart)) : null;
        const saleEnd = course.saleEnd ? dateToDateValue(new Date(course.saleEnd)) : null;

        const saleDateRange =
          saleStart && saleEnd
            ? ({ start: saleStart, end: saleEnd } as { start: CalendarDate; end: CalendarDate })
            : null;

        setValue('title', course.title || '');
        setValue('type', course.type || '');
        setValue('level', course.level || '');
        const categoryIdFromResponse =
          getCategoryId(courseData.data.categories?.[0]) ||
          getCategoryId((course as { category?: unknown }).category);
        const categoryTitleFromResponse =
          getCategoryTitle(courseData.data.categories?.[0]) ||
          getCategoryTitle((course as { category?: unknown }).category);
        const categoryIdByTitle =
          categoryItems.find((item) => item.label === categoryTitleFromResponse)?.key || '';
        setValue('category', categoryIdFromResponse || categoryIdByTitle);
        setValue('leadTeacher', String(course.leadTeacher?.id || ''));
        setValue('description', course.description || '');
        setValue('price', course.price || 0);
        setValue('discountPrice', course.discountPrice || undefined);
        setValue('saleDateRange', saleDateRange);
        setValue('durationFullTime', course.durationFullTime || 0);
        setValue('durationPartTime', course.durationPartTime || 0);
        setValue('certificateDescription', course.certificateDescription || '');
        setValue('seoTitle', course.seoTitle || '');
        setValue('seoDescription', course.seoDescription || '');
        setValue('isAI', course.isAI || false);
        setValue('pathway', course.pathway || '');
        setValue('paymentLink', course.paymentLink || '');
        setValue('imageFile', null);
        setValue('previewFile', null);

        if (course.benefits && course.benefits.length > 0) {
          const benefitsData = course.benefits.map((b, index) => ({
            id: String(index + 1),
            title: b.title || '',
            description: b.description || '',
          }));
          setBenefits(
            benefitsData.length > 0 ? benefitsData : [{ id: '1', title: '', description: '' }],
          );
        } else {
          setBenefits([{ id: '1', title: '', description: '' }]);
        }

        if (course.targetAudience && course.targetAudience.length > 0) {
          const targetAudienceData = course.targetAudience.map((t, index) => ({
            id: String(index + 1),
            title: t.title || '',
            description: t.description || '',
          }));
          setTargetAudience(
            targetAudienceData.length > 0
              ? targetAudienceData
              : [{ id: '1', title: '', description: '' }],
          );
        } else {
          setTargetAudience([{ id: '1', title: '', description: '' }]);
        }

        if (
          (course.recommendedCourses && course.recommendedCourses.length > 0) ||
          (courseData.data.recommendedCourses && courseData.data.recommendedCourses.length > 0)
        ) {
          const recommended = course.recommendedCourses || courseData.data.recommendedCourses || [];
          const recommendedIds = recommended.map((c) => c.id);
          setRecommendedCourses(recommendedIds);
          setInitialRecommendedCourses(recommendedIds);
        } else {
          setRecommendedCourses([]);
          setInitialRecommendedCourses([]);
        }

        setShowDiscountPrice(!!course.discountPrice);
        setCurrentImageUrl(course.image || null);
        setCurrentPreviewUrl(course.preview || null);
      }
    }, [courseData?.data?.course, courseData?.data?.categories, isOpen, setValue, categoryItems]);

    const teacherItems = academicStaff.map((staff: AcademicStaff) => ({
      key: String(staff.id),
      label: `${staff.firstName} ${staff.lastName}`,
    }));

    const validateCoursePage = () => {
      const isValid =
        !hasCoursePointErrors(benefits) &&
        !hasCoursePointErrors(targetAudience) &&
        benefits.filter((b) => b.title.trim() && b.description.trim()).length <=
          COURSE_MAX_BENEFITS;
      setShowPointErrors(!isValid);
      return isValid;
    };

    const onSubmit = async (data: CourseFormData) => {
      if (!validateCoursePage()) {
        setActiveTab('course-page');
        return;
      }

      try {
        const benefitsData = benefits
          .filter((b) => b.title.trim() && b.description.trim())
          .map((b) => ({ title: b.title.trim(), description: b.description.trim() }));

        const targetAudienceData = targetAudience
          .filter((t) => t.title.trim() && t.description.trim())
          .map((t) => ({ title: t.title.trim(), description: t.description.trim() }));

        const certificateDescription = data.certificateDescription?.trim() || '';
        const seoTitle = data.seoTitle?.trim() || '';
        const seoDescription = data.seoDescription?.trim() || '';

        let saleStart: string | undefined;
        let saleEnd: string | undefined;

        if (data.saleDateRange && data.saleDateRange.start && data.saleDateRange.end) {
          const formatDateToISO = (date: CalendarDate): string => {
            const dateObj = new Date(date.year, date.month - 1, date.day, 10, 0, 0);
            return dateObj.toISOString();
          };
          saleStart = formatDateToISO(data.saleDateRange.start);
          saleEnd = formatDateToISO(data.saleDateRange.end);
        }

        const result = await updateCourse({
          id: courseId,
          body: {
            title: data.title,
            type: data.type,
            level: data.level,
            category: Number(data.category),
            price: data.price,
            discountPrice: showDiscountPrice ? data.discountPrice : undefined,
            leadTeacher: Number(data.leadTeacher),
            description: data.description || '',
            saleStart: saleStart,
            saleEnd: saleEnd,
            benefits: benefitsData.length > 0 ? benefitsData : undefined,
            targetAudience: targetAudienceData.length > 0 ? targetAudienceData : undefined,
            recommendedCourses: recommendedCourses,
            isAI: data.type === CourseType.SHORT && data.isAI,
            durationFullTime: data.durationFullTime,
            durationPartTime: data.durationPartTime,
            certificateDescription,
            seoTitle,
            seoDescription,
            pathway: data.pathway || undefined,
            paymentLink: data.paymentLink?.trim() || '',
            imageFile: data.imageFile || undefined,
            previewFile: data.previewFile || undefined,
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Course updated successfully',
          color: 'success',
        });

        setSelectedSection(null);
        setActiveTab('cover');
        onSuccess?.();
      } catch (error: any) {
        const message = error?.data?.message;
        const field = getCourseServerErrorField(message);
        if (field) {
          setError(field as keyof CourseFormData, { type: 'server', message });
          const tab = getCourseFormErrorTab([field]);
          if (tab) setActiveTab(tab);
        }
        toast({
          title: 'Error',
          description: message || 'Failed to update course',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset();
      setBenefits([{ id: '1', title: '', description: '' }]);
      setTargetAudience([{ id: '1', title: '', description: '' }]);
      setRecommendedCourses([]);
      setValue('saleDateRange', null as any);
      setValue('durationFullTime', 0);
      setValue('durationPartTime', 0);
      setShowDiscountPrice(false);
      setCurrentImageUrl(null);
      setCurrentPreviewUrl(null);
      setActiveTab('cover');
      setSelectedSection(null);
      setShowPointErrors(false);
      setShowRecommendedLimitError(false);
      onOpenChange(false);
    };

    const handleDeleteCourse = async () => {
      try {
        await deleteCourse(courseId).unwrap();
        toast({
          title: 'Success',
          description: 'Course deleted successfully',
          color: 'success',
        });
        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to delete course',
          color: 'danger',
        });
      }
      onDeleteModalOpenChange();
    };

    const handleSectionSelect = (section: string) => {
      setSelectedSection(section);
      setActiveTab(section);
    };

    const handleNext = async () => {
      const isValid =
        activeTab === 'course-page'
          ? validateCoursePage()
          : await trigger(
              (COURSE_FORM_TAB_FIELDS[activeTab as keyof typeof COURSE_FORM_TAB_FIELDS] ??
                []) as Array<keyof CourseFormData>,
            );
      if (!isValid) return;

      if (activeTab === 'cover') {
        setActiveTab('course-page');
      } else if (activeTab === 'course-page') {
        setActiveTab('settings');
      }
    };

    if (isCourseLoading) {
      return null;
    }

    const course = courseData?.data?.course;
    const BASE_API = import.meta.env.VITE_API_URL_BASE || '';

    return (
      <>
        <Modal
          isOpen={isOpen && !selectedSection}
          onOpenChange={handleClose}
          placement="center"
          motionProps={scaleInOut}
          size="xl"
          classNames={{
            wrapper: '!items-start overflow-y-auto py-4',
            base: 'p-6 flex flex-col gap-5 !my-0 max-w-[min(740px,calc(100vw-2rem))]',
            closeButton: styles.closeBtn,
          }}
        >
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className="flex items-center justify-between">
                  <h2 className={styles.title}>View course</h2>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSectionSelect('cover')}
                      className="p-2 hover:bg-grey-10 rounded-lg transition-colors text-grey-secondary"
                      aria-label="Edit course"
                    >
                      <Svg Icon={EditIcon} width={20} height={20} />
                    </button>
                    {canCourseDelete && (
                      <button
                        type="button"
                        onClick={onDeleteModalOpen}
                        className="p-2 hover:bg-grey-10 rounded-lg transition-colors text-icon-dizlike"
                        aria-label="Delete course"
                      >
                        <Svg Icon={TrashIcon} width={20} height={20} />
                      </button>
                    )}
                  </div>
                </ModalHeader>
                <ModalBody className="flex flex-col overflow-visible p-0">
                  {isCourseLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="text-grey-secondary">Loading...</div>
                    </div>
                  ) : course ? (
                    <div className="flex flex-col gap-6">
                      <div className="relative h-[min(400px,40vh)] w-full shrink-0 overflow-hidden rounded-lg">
                        <img
                          src={course.image}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        {courseData?.data?.categories?.[0] && (
                          <span className="px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm">
                            {getCategoryTitle(courseData?.data?.categories?.[0]) ||
                              categoryItems.find(
                                (category) =>
                                  category.key === getCategoryId(courseData?.data?.categories?.[0]),
                              )?.label}
                          </span>
                        )}
                        <span className="px-3 py-1 bg-grey-10 text-grey-secondary rounded-full text-sm">
                          {ALL_COURSE_LEVELS.find((l) => l.key === course.level)?.label || course.level}
                        </span>
                      </div>
                      <h3 className="break-words text-2xl font-bold">{course.title}</h3>
                      {course.leadTeacher && (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-grey-10 flex items-center justify-center overflow-hidden">
                            {course.leadTeacher.avatar ? (
                              <img
                                src={`${course.leadTeacher.avatar}`}
                                alt={`${course.leadTeacher.firstName} ${course.leadTeacher.lastName}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-grey-secondary text-sm">
                                {course.leadTeacher.firstName?.[0]}
                                {course.leadTeacher.lastName?.[0]}
                              </span>
                            )}
                          </div>
                          <span className="text-base">
                            {course.leadTeacher.firstName} {course.leadTeacher.lastName}
                          </span>
                        </div>
                      )}
                      {course.description && (
                        <div className="break-words whitespace-pre-wrap text-grey-secondary">
                          {course.description}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-10">
                      <div className="text-grey-secondary">Course not found</div>
                    </div>
                  )}
                </ModalBody>
              </>
            )}
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isOpen && !!selectedSection}
          onOpenChange={() => {
            setSelectedSection(null);
            setActiveTab('cover');
          }}
          placement="center"
          motionProps={scaleInOut}
          size="5xl"
          classNames={{
            base: 'p-6 flex flex-col gap-5 max-w-[740px]',
            closeButton: styles.closeBtn,
            wrapper: styles.modalContent,
          }}
        >
          <ModalContent>
            {(onClose) => (
              <form onSubmit={handleSubmit(onSubmit as any)}>
                <ModalHeader className={styles.title}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedSection(null)}
                      className="text-grey-secondary hover:text-black transition-colors"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.5 15L7.5 10L12.5 5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <span>Edit course</span>
                  </div>
                </ModalHeader>
                <ModalBody className="p-0 flex flex-col overflow-hidden">
                  <Tabs
                    aria-label="Course edit tabs"
                    selectedKey={activeTab}
                    onSelectionChange={(key) => setActiveTab(key as string)}
                    classNames={{
                      tabList: 'gap-0 bg-transparent',
                      tab: 'px-6 py-3 bg-transparent data-[selected=true]:bg-[#1E1E1E] rounded-lg',
                      tabContent: 'data-[selected=true]:!text-[#FFFFFF]',
                      panel: 'pt-6',
                    }}
                  >
                    <Tab key="cover" title="Cover">
                      <div className="flex gap-6">
                        <div className="flex-1">
                          <Controller
                            name="imageFile"
                            control={control}
                            render={({ field }) => (
                              <CustomFileInput
                                value={field.value}
                                onChange={field.onChange}
                                accept={COURSE_IMAGE_ACCEPT}
                                initialPreviewPicture={currentImageUrl}
                                errorMessage={errors.imageFile?.message}
                                showError={!!errors.imageFile}
                              />
                            )}
                          />
                        </div>
                        <div className="flex-1 flex flex-col gap-4">
                          <Controller
                            name="title"
                            control={control}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                label="Course Title"
                                labelPlacement="outside"
                                placeholder="Enter course title"
                                errorMessage={errors.title?.message}
                                showError={!!errors.title}
                                maxChars={COURSE_TEXT_LIMITS.title}
                                isRequired
                              />
                            )}
                          />
                          <Controller
                            name="type"
                            control={control}
                            render={({ field }) => (
                              <CustomSelect
                                style="black"
                                label="Type of course"
                                labelPlacement="outside"
                                placeholder="Select type"
                                items={COURSE_TYPES}
                                selectedKeys={field.value ? new Set([field.value]) : new Set()}
                                onSelectionChange={(keys) => {
                                  const selected = Array.from(keys)[0] as string;
                                  field.onChange(selected);
                                }}
                                errorMessage={errors.type?.message}
                                showError={!!errors.type}
                                isRequired
                              />
                            )}
                          />
                          <Controller
                            name="category"
                            control={control}
                            render={({ field }) => (
                              <CustomSelect
                                style="black"
                                label="Category"
                                labelPlacement="outside"
                                placeholder="Select course category"
                                items={categoryItems}
                                selectedKeys={field.value ? new Set([field.value]) : new Set()}
                                onSelectionChange={(keys) => {
                                  const selected = Array.from(keys)[0] as string;
                                  field.onChange(selected);
                                }}
                                errorMessage={errors.category?.message}
                                showError={!!errors.category}
                                isRequired
                              />
                            )}
                          />
                          {watchedType === CourseType.SHORT && (
                            <Controller
                              name="isAI"
                              control={control}
                              render={({ field }) => (
                                <CustomCheckboxField
                                  label="Classification"
                                  title="AI course"
                                  description="The course gets the AI course classification"
                                  isSelected={!!field.value}
                                  onValueChange={field.onChange}
                                />
                              )}
                            />
                          )}
                          <Controller
                            name="level"
                            control={control}
                            render={({ field }) => (
                              <CustomSelect
                                style="black"
                                label="Difficulty Level"
                                labelPlacement="outside"
                                placeholder={
                                  watchedType
                                    ? 'Select difficulty level'
                                    : 'Select type of course first'
                                }
                                items={levelItems}
                                selectedKeys={field.value ? new Set([field.value]) : new Set()}
                                onSelectionChange={(keys) => {
                                  const selected = Array.from(keys)[0] as string;
                                  field.onChange(selected);
                                }}
                                errorMessage={errors.level?.message}
                                showError={!!errors.level}
                                isRequired
                                isDisabled={!watchedType}
                              />
                            )}
                          />
                          <Controller
                            name="leadTeacher"
                            control={control}
                            render={({ field }) => (
                              <CustomSelect
                                style="black"
                                label="Course Lead"
                                labelPlacement="outside"
                                placeholder="Select course lead"
                                items={teacherItems}
                                selectedKeys={field.value ? new Set([field.value]) : new Set()}
                                onSelectionChange={(keys) => {
                                  const selected = Array.from(keys)[0] as string;
                                  field.onChange(selected);
                                }}
                                errorMessage={errors.leadTeacher?.message}
                                showError={!!errors.leadTeacher}
                                isRequired
                              />
                            )}
                          />
                          <Controller
                            name="pathway"
                            control={control}
                            render={({ field }) => (
                              <CustomSelect
                                style="black"
                                label="Pathway"
                                labelPlacement="outside"
                                placeholder="Select pathway"
                                items={COURSE_PATHWAYS}
                                selectedKeys={field.value ? new Set([field.value]) : new Set()}
                                onSelectionChange={(keys) => {
                                  const selected = Array.from(keys)[0] as string;
                                  field.onChange(selected);
                                }}
                              />
                            )}
                          />
                          <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                              <CustomTextarea
                                {...field}
                                label="Course description"
                                labelPlacement="outside"
                                placeholder="Enter point description"
                                minRows={3}
                                errorMessage={errors.description?.message}
                                showError={!!errors.description}
                                maxChars={COURSE_TEXT_LIMITS.description}
                                isRequired
                              />
                            )}
                          />
                        </div>
                      </div>
                    </Tab>
                    <Tab key="course-page" title="Course page">
                      <div className="flex flex-col gap-8 max-h-[calc(100vh-350px)] overflow-y-auto pr-4">
                        <div className="flex flex-col gap-2">
                          <h3 className="text-lg font-semibold">What you&#39;ll learn</h3>
                          <p className="text-sm text-grey-secondary">
                            Add up to 6 key points that students will master during the course
                          </p>
                          <div className="flex flex-col gap-4">
                            {benefits.map((benefit, index) => {
                              const pointErrors = getCoursePointErrors(benefit, showPointErrors);
                              const hasError = !!pointErrors.title || !!pointErrors.description;
                              return (
                                <div
                                  key={benefit.id}
                                  className={`flex flex-col gap-2 p-4 border rounded-lg ${
                                    hasError
                                      ? 'border-red-tertiary bg-red-secondary/10'
                                      : 'border-grey-border'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-grey-secondary">
                                      Point {index + 1}
                                    </span>
                                    {benefits.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setBenefits(benefits.filter((_, i) => i !== index))
                                        }
                                        className="text-red-tertiary hover:opacity-80"
                                        aria-label="Remove point"
                                      >
                                        <svg
                                          width="20"
                                          height="20"
                                          viewBox="0 0 20 20"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15 5L5 15M5 5L15 15"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                          />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                  <CustomInput
                                    value={benefit.title}
                                    onValueChange={(value) => {
                                      const newBenefits = [...benefits];
                                      newBenefits[index].title = value;
                                      setBenefits(newBenefits);
                                    }}
                                    placeholder="Enter point title"
                                    className="w-full"
                                    errorMessage={pointErrors.title}
                                    showError={!!pointErrors.title}
                                    maxChars={COURSE_TEXT_LIMITS.pointTitle}
                                  />
                                  <CustomTextarea
                                    value={benefit.description}
                                    onValueChange={(value) => {
                                      const newBenefits = [...benefits];
                                      newBenefits[index].description = value || '';
                                      setBenefits(newBenefits);
                                    }}
                                    placeholder="Enter point description"
                                    minRows={3}
                                    className="w-full"
                                    errorMessage={pointErrors.description}
                                    showError={!!pointErrors.description}
                                  />
                                </div>
                              );
                            })}
                            {benefits.length < 6 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setBenefits([
                                    ...benefits,
                                    { id: String(Date.now()), title: '', description: '' },
                                  ])
                                }
                                className="text-grey-secondary text-sm self-start flex items-center gap-1 hover:text-black transition-colors"
                              >
                                + Add more
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <h3 className="text-lg font-semibold">This program is perfect for</h3>
                          <p className="text-sm text-grey-secondary">
                            Add key points describing who this program is best suited for.
                          </p>
                          <div className="flex flex-col gap-4">
                            {targetAudience.map((item, index) => {
                              const pointErrors = getCoursePointErrors(item, showPointErrors);
                              const hasError = !!pointErrors.title || !!pointErrors.description;
                              return (
                                <div
                                  key={item.id}
                                  className={`flex flex-col gap-2 p-4 border rounded-lg ${
                                    hasError
                                      ? 'border-red-tertiary bg-red-secondary/10'
                                      : 'border-grey-border'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-grey-secondary">
                                      Point {index + 1}
                                    </span>
                                    {targetAudience.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setTargetAudience(
                                            targetAudience.filter((_, i) => i !== index),
                                          )
                                        }
                                        className="text-red-tertiary hover:opacity-80"
                                        aria-label="Remove point"
                                      >
                                        <svg
                                          width="20"
                                          height="20"
                                          viewBox="0 0 20 20"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15 5L5 15M5 5L15 15"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                          />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                  <CustomInput
                                    value={item.title}
                                    onValueChange={(value) => {
                                      const newTargetAudience = [...targetAudience];
                                      newTargetAudience[index].title = value;
                                      setTargetAudience(newTargetAudience);
                                    }}
                                    placeholder="Enter point title"
                                    className="w-full"
                                    errorMessage={pointErrors.title}
                                    showError={!!pointErrors.title}
                                    maxChars={COURSE_TEXT_LIMITS.pointTitle}
                                  />
                                  <CustomTextarea
                                    value={item.description}
                                    onValueChange={(value) => {
                                      const newTargetAudience = [...targetAudience];
                                      newTargetAudience[index].description = value || '';
                                      setTargetAudience(newTargetAudience);
                                    }}
                                    placeholder="Enter point description"
                                    minRows={3}
                                    className="w-full"
                                    errorMessage={pointErrors.description}
                                    showError={!!pointErrors.description}
                                  />
                                </div>
                              );
                            })}
                            {targetAudience.length < 10 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setTargetAudience([
                                    ...targetAudience,
                                    { id: String(Date.now()), title: '', description: '' },
                                  ])
                                }
                                className="text-grey-secondary text-sm self-start flex items-center gap-1 hover:text-black transition-colors"
                              >
                                + Add more
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <h3 className="text-lg font-semibold">Recommended courses</h3>
                          <p className="text-sm text-grey-secondary">
                            Add 3 recommended courses for students of this one
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              if (recommendedCourses.length >= COURSE_MAX_RECOMMENDED) {
                                setShowRecommendedLimitError(true);
                                return;
                              }
                              setIsCourseSelectModalOpen(true);
                            }}
                            className={`text-grey-secondary text-sm self-start flex items-center gap-1 hover:text-black transition-colors border rounded-lg px-4 py-2 ${
                              showRecommendedLimitError ? 'border-red-tertiary' : 'border-grey-border'
                            }`}
                          >
                            + Add recommended courses
                          </button>
                          {showRecommendedLimitError && (
                            <p className="text-xs text-red-tertiary" role="alert">
                              {COURSE_RECOMMENDED_LIMIT_MESSAGE}
                            </p>
                          )}
                          {recommendedCourses.length > 0 && (
                            <div className="flex flex-col gap-3 mt-2">
                              {recommendedCourses.map((courseId) => {
                                const course = allCourses.find((c) => c.id === courseId);
                                if (!course) return null;
                                return (
                                  <div
                                    key={courseId}
                                    className="flex items-center gap-3 p-3 border border-grey-border rounded-lg relative"
                                  >
                                    <div className="w-16 h-16 bg-grey-10 rounded-lg flex items-center justify-center">
                                      <span className="text-grey-secondary text-xs">Image</span>
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="text-sm font-semibold">{course.title}</h4>
                                      <span className="inline-block mt-1 text-xs text-red-tertiary bg-red-secondary px-2 py-1 rounded-full">
                                        Master&#39;s Degree
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setRecommendedCourses(
                                          recommendedCourses.filter((id) => id !== courseId),
                                        );
                                        setShowRecommendedLimitError(false);
                                      }}
                                      className="absolute top-2 right-2 text-grey-secondary hover:text-black"
                                      aria-label="Remove course"
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M12 4L4 12M4 4L12 12"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </Tab>
                    <Tab key="settings" title="Settings">
                      <div className="flex flex-col gap-6 max-h-[calc(100vh-350px)] overflow-y-auto pr-4">
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col gap-1">
                            <h3 className="text-lg font-semibold">Course duration</h3>
                            <p className="text-sm text-grey-secondary">
                              Set at least one of the durations
                            </p>
                          </div>
                          <div className="flex flex-col gap-4">
                            <Controller
                              name="durationFullTime"
                              control={control}
                              render={({ field }) => (
                                <div className="flex flex-col gap-1">
                                  <label className="text-sm text-grey-secondary">
                                    Course Duration at Full Time
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <CustomInput
                                      {...field}
                                      value={field.value?.toString() || ''}
                                      onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                                      type="number"
                                      placeholder="Enter duration"
                                      className="flex-1"
                                      errorMessage={errors.durationFullTime?.message}
                                      showError={!!errors.durationFullTime}
                                    />
                                    <span className="text-sm text-grey-secondary whitespace-nowrap">
                                      Months
                                    </span>
                                  </div>
                                </div>
                              )}
                            />
                            <Controller
                              name="durationPartTime"
                              control={control}
                              render={({ field }) => (
                                <div className="flex flex-col gap-1">
                                  <label className="text-sm text-grey-secondary">
                                    Course Duration at Part Time
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <CustomInput
                                      {...field}
                                      value={field.value?.toString() || ''}
                                      onChange={(e) => {
                                        field.onChange(Number(e.target.value) || 0);
                                        if (errors.durationFullTime) trigger('durationFullTime');
                                      }}
                                      type="number"
                                      placeholder="Enter duration"
                                      className="flex-1"
                                      errorMessage={errors.durationPartTime?.message}
                                      showError={!!errors.durationPartTime}
                                    />
                                    <span className="text-sm text-grey-secondary whitespace-nowrap">
                                      Months
                                    </span>
                                  </div>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <h3 className="text-lg font-semibold">Price</h3>
                          <Controller
                            name="price"
                            control={control}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                value={field.value?.toString() || ''}
                                onChange={(e) => {
                                  field.onChange(Number(e.target.value) || 0);
                                  if (getValues('discountPrice') !== undefined) {
                                    trigger('discountPrice');
                                  }
                                }}
                                type="number"
                                label="Course Price"
                                labelPlacement="outside"
                                placeholder="Enter price"
                                startContent={<span className="text-grey-secondary">£</span>}
                                errorMessage={errors.price?.message}
                                showError={!!errors.price}
                                isRequired
                              />
                            )}
                          />
                          <Controller
                            name="paymentLink"
                            control={control}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                label="Payment link"
                                labelPlacement="outside"
                                placeholder="https://payments-eu1.hubspot.com/payments/..."
                                errorMessage={errors.paymentLink?.message}
                                showError={!!errors.paymentLink}
                                maxChars={COURSE_TEXT_LIMITS.paymentLink}
                              />
                            )}
                          />
                          <Checkbox
                            isSelected={showDiscountPrice}
                            onValueChange={(checked) => {
                              setShowDiscountPrice(checked);
                              if (!checked) {
                                setValue('discountPrice', undefined as any);
                              }
                            }}
                            classNames={{
                              base: 'rounded py-1',
                              icon: 'text-white',
                            }}
                          >
                            <span className="!text-black text-sm text-grey-secondary">
                              Discounted Price
                            </span>
                          </Checkbox>
                          {showDiscountPrice && (
                            <Controller
                              name="discountPrice"
                              control={control}
                              render={({ field }) => (
                                <CustomInput
                                  {...field}
                                  value={field.value?.toString() || ''}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value) || undefined)
                                  }
                                  type="number"
                                  label="Discounted price, £"
                                  labelPlacement="outside"
                                  placeholder="Enter discount price"
                                  errorMessage={errors.discountPrice?.message}
                                  showError={!!errors.discountPrice}
                                />
                              )}
                            />
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <h3 className="text-lg font-semibold">Certificate description</h3>
                          <Controller
                            name="certificateDescription"
                            control={control}
                            render={({ field }) => (
                              <CustomTextarea
                                {...field}
                                placeholder="Optional text printed on the certificate (150–600 characters)"
                                minRows={4}
                                errorMessage={errors.certificateDescription?.message}
                                showError={!!errors.certificateDescription}
                                minChars={COURSE_TEXT_LIMITS.certificateDescriptionMin}
                                maxChars={COURSE_TEXT_LIMITS.certificateDescriptionMax}
                              />
                            )}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <h3 className="text-lg font-semibold">SEO</h3>
                          <Controller
                            name="seoTitle"
                            control={control}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                label="SEO title"
                                labelPlacement="outside"
                                placeholder="Maximum 100 characters"
                                maxLength={COURSE_TEXT_LIMITS.seoTitle}
                                maxChars={COURSE_TEXT_LIMITS.seoTitle}
                                errorMessage={errors.seoTitle?.message}
                                showError={!!errors.seoTitle}
                                onChange={(event) =>
                                  field.onChange(
                                    event.target.value.slice(0, COURSE_TEXT_LIMITS.seoTitle),
                                  )
                                }
                              />
                            )}
                          />
                          <Controller
                            name="seoDescription"
                            control={control}
                            render={({ field }) => (
                              <CustomTextarea
                                {...field}
                                label="SEO description"
                                labelPlacement="outside"
                                placeholder="Maximum 200 characters"
                                maxLength={COURSE_TEXT_LIMITS.seoDescription}
                                maxChars={COURSE_TEXT_LIMITS.seoDescription}
                                minRows={4}
                                errorMessage={errors.seoDescription?.message}
                                showError={!!errors.seoDescription}
                                onChange={(event) =>
                                  field.onChange(
                                    event.target.value.slice(0, COURSE_TEXT_LIMITS.seoDescription),
                                  )
                                }
                              />
                            )}
                          />
                        </div>
                      </div>
                    </Tab>
                  </Tabs>
                </ModalBody>
                <ModalFooter className="flex justify-center items-center gap-4 p-0">
                  <AppButton
                    fullWidth
                    color="white"
                    onPress={() => {
                      if (selectedSection) {
                        setSelectedSection(null);
                        setActiveTab('cover');
                      } else {
                        onClose();
                      }
                    }}
                    type="button"
                  >
                    {selectedSection ? 'Back' : 'Cancel'}
                  </AppButton>
                  {activeTab !== 'settings' ? (
                    <AppButton
                      fullWidth
                      color="black"
                      onPress={handleNext}
                      type="button"
                    >
                      Next
                    </AppButton>
                  ) : (
                    <AppButton
                      fullWidth
                      color="black"
                      type="button"
                      isLoading={isLoading}
                      onPress={() => {
                        handleSubmit(onSubmit as any, (errors) => {
                          const tab = getCourseFormErrorTab(Object.keys(errors));
                          if (tab) setActiveTab(tab);
                          const firstError = Object.values(errors)[0];
                          if (firstError?.message) {
                            toast({
                              title: 'Validation Error',
                              description: firstError.message as string,
                              color: 'danger',
                            });
                          }
                        })();
                      }}
                    >
                      Edit course
                    </AppButton>
                  )}
                </ModalFooter>
              </form>
            )}
          </ModalContent>
          <Modal
            isOpen={isCourseSelectModalOpen}
            onOpenChange={(open) => {
              setIsCourseSelectModalOpen(open);
              if (!open) setShowSelectLimitError(false);
            }}
            placement="center"
            motionProps={scaleInOut}
            size="2xl"
            classNames={{
              base: 'p-6 flex flex-col gap-5 max-w-[600px]',
              closeButton: styles.closeBtn,
            }}
          >
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className={styles.title}>Select Recommended Courses</ModalHeader>
                  <ModalBody className="p-0 flex flex-col gap-3 max-h-[400px] overflow-y-auto">
                    {allCourses
                      .filter(
                        (course) =>
                          course.id !== courseId && !recommendedCourses.includes(course.id),
                      )
                      .map((course) => (
                        <button
                          key={course.id}
                          type="button"
                          onClick={() => {
                            if (recommendedCourses.length < COURSE_MAX_RECOMMENDED) {
                              setRecommendedCourses([...recommendedCourses, course.id]);
                            } else {
                              setShowSelectLimitError(true);
                            }
                          }}
                          className={`flex items-center gap-3 p-3 border border-grey-border rounded-lg transition-colors text-left w-full ${
                            recommendedCourses.length >= COURSE_MAX_RECOMMENDED
                              ? 'opacity-60 cursor-not-allowed'
                              : 'hover:bg-grey-10'
                          }`}
                          aria-disabled={recommendedCourses.length >= COURSE_MAX_RECOMMENDED}
                        >
                          <div className="w-12 h-12 bg-grey-10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-grey-secondary text-xs">Img</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold truncate">{course.title}</h4>
                            <span className="inline-block mt-1 text-xs text-red-tertiary bg-red-secondary px-2 py-1 rounded-full">
                              Master&#39;s Degree
                            </span>
                          </div>
                        </button>
                      ))}
                    {allCourses.filter(
                      (course) => course.id !== courseId && !recommendedCourses.includes(course.id),
                    ).length === 0 && (
                      <p className="text-sm text-grey-secondary text-center py-4">
                        No more courses available
                      </p>
                    )}
                  </ModalBody>
                  <ModalFooter className="flex-col gap-3">
                    {showSelectLimitError && (
                      <p className="text-xs text-red-tertiary" role="alert">
                        {COURSE_RECOMMENDED_SELECTED_MESSAGE}
                      </p>
                    )}
                    <AppButton color="black" onPress={onClose} fullWidth>
                      Done
                    </AppButton>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </Modal>
        <MessageModal
          title="Delete Course"
          text={`Are you sure you want to delete the course "${course?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          isOpen={isDeleteModalOpen}
          onOpenChange={onDeleteModalOpenChange}
          variant="red"
          toggleVisibility={handleDeleteCourse}
          isLoading={isDeleting}
        />
      </>
    );
  },
);
